import type { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';

import { IgnoredError, ReportError, useMutation } from '@furality/frontend-stack';
import { Button, TimeAgoMessage } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import useReload from '@/lib/hooks/useReload.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useToasts from '@/lib/useToast.ts';

export type PartyInviteToastBodyProps = {
  id: string;
  timestamp: number;
  requesterVrchatId: string;
  requesterDisplayName: string;
} & ComponentProps<'div'>;

const PartyInviteToastBody: FC<PartyInviteToastBodyProps> = ({
  id,
  timestamp,
  requesterVrchatId,
  requesterDisplayName,

  ...divProps
}) => {
  const { pushToast, removeToast } = useToasts();
  const { t } = useTranslation(['common', 'party']);

  const reloadPartyQuery = useReload({
    queryKey: [QUERY_KEYS.Party.Party],
  });

  const acceptPartyMember = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fox.value.partyAcceptUser({ body: { vrchatId: userId } });
      switch (response.status) {
        case 204:
          removeToast(id);
          await reloadPartyQuery();
          return 'ok';

        case 403:
          pushToast('error', t('party:party.errors.not_the_owner'));
          throw new IgnoredError(response);

        /*
        case 400:
          pushToast('error', t('party:party.errors.accept_unknown_user'));
          removeToast(id);
          reloadPartyQuery();
          return ErrorHandled.Handled;
       */

        default:
          pushToast('error', t('shared.errors.unknown_error_try_later'));
          removeToast(id);
          throw new ReportError(response);
      }
    },
  });

  const kickPartyMember = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fox.value.partyKickUser({ body: { vrchatId: userId } });
      switch (response.status) {
        case 204:
          removeToast(id);
          void reloadPartyQuery();
          return 'ok';

        case 403:
          pushToast('error', t('party:party.errors.not_the_owner'));
          throw new IgnoredError(response);

        /*
       case 400:
         pushToast('error', t('party:party.errors.kicked_unknown_user'));
         removeToast(id);
         reloadPartyQuery();
         return ErrorHandled.Handled;
          */
        default:
          pushToast('error', t('shared.errors.unknown_error_try_later'));
          removeToast(id);
          throw new ReportError(response);
      }
    },
  });

  return (
    <div {...divProps}>
      <div className="toast-header text-white bg-gradient-primary">
        <strong className="mr-auto">{t('party_invitation')}</strong>
        {timestamp && <TimeAgoMessage timestampUnix={timestamp} />}
        <button
          type="button"
          className="ml-2 close opacity-10 unstyled-button"
          aria-label={t('shared.close.aria_label')}
          onClick={() => removeToast(id)}
        >
          <span className="text-white" aria-hidden="true">
            &times;
          </span>
        </button>
      </div>
      <div className="toast-body text-white bg-light">
        {t('socket.join_request', { name: requesterDisplayName })}
        <div className="mt-2">
          <Button
            variant="success"
            size="sm"
            rounded
            onClick={() => acceptPartyMember.mutate(requesterVrchatId)}
            disabled={acceptPartyMember.isPending || kickPartyMember.isPending}
          >
            {t('party:party.accept')}
          </Button>
          <Button
            className="ml-auto"
            variant="secondary"
            size="sm"
            rounded
            onClick={() => kickPartyMember.mutate(requesterVrchatId)}
            disabled={acceptPartyMember.isPending || kickPartyMember.isPending}
          >
            {t('party:party.reject')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PartyInviteToastBody;
